/**
 * GroupMeshNode — Combines multiple mesh pipelines into a single output.
 *
 * Topology-only node: the scene builder walks upstream through all connections
 * to the input and collects separate MeshEntries for each — preserving individual
 * materials and transforms. Unlike MergeGeometry, this keeps meshes
 * as distinct objects (separate materials, shadows, etc.).
 *
 * Use case: combine walls + roof into one output that a Switch or
 * definition API can expose as a single connection.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals } from '@/nodes/TypedEndpoints';
import type { Dependencies } from '@nodes/Dependencies';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';

const groupMeshEndpoints = {
  ...internals({
    in: { dataType: 'three:group', isInput: true, label: 'Input' },
    out: { dataType: 'three:group', isOutput: true, label: 'Output' },
  }),
};

class GroupMeshNode extends BaseNode<typeof groupMeshEndpoints> {
  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {}

  onInternalChanged(payload: Payload, endpointId: string): void {
    if (endpointId === 'in') {
      this.internals.out.set(payload?.value ?? payload);
    }
  }
}

export const groupMeshNodeDef: NodeDef<GroupMeshNode> = {
  type: 'GroupMesh',
  shortName: 'group',
  info: 'Combines multiple mesh inputs into one output, preserving individual materials and transforms.',
  category: 'geometry',
  endpoints: groupMeshEndpoints,
  ctor: GroupMeshNode,
};
